import { parseImageValidationTarget } from "./image-validation-target";
import type { BrowserImageValidationSession } from "./image-validation-session";

const bridges = new Map<string, ImageValidationWorkerBridge>();
const renderListeners = new Map<string, Set<() => void>>();
const renderVersions = new Map<string, number>();

export function imageValidationRenderingVersion(appID: string): number {
    return renderVersions.get(appID) ?? 0;
}

/** Observers refresh only the explicitly selected app's rendered media. */
export function subscribeImageValidationRendering(appID: string, listener: () => void): () => void {
    let listeners = renderListeners.get(appID);
    if (listeners === undefined) {
        listeners = new Set();
        renderListeners.set(appID, listeners);
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) renderListeners.delete(appID);
    };
}

function refreshRendering(appID: string): void {
    renderVersions.set(appID, imageValidationRenderingVersion(appID) + 1);
    for (const listener of renderListeners.get(appID) ?? []) listener();
}

/** Called only at render sites with an explicit app ID. */
export function validationImageURL(appID: string, source: string, retirementTarget?: () => string): string {
    return bridges.get(appID)?.imageURL(source, retirementTarget) ?? source;
}

/** Browser/worker handshake is mandatory; old or uncontrolled workers cannot activate. */
export class ImageValidationWorkerBridge {
    private controller: ServiceWorker | undefined;
    private timer: ReturnType<typeof setInterval> | undefined;
    private ready = false;
    private readonly instanceID = Array.from(crypto.getRandomValues(new Uint32Array(4)), n => n.toString(16)).join("-");
    private generation = 0;
    private starting: number | undefined;
    private revision = "";

    constructor(
        private readonly appID: string,
        private readonly endpoint: string,
        private readonly session: BrowserImageValidationSession,
        private readonly workers: ServiceWorkerContainer
    ) {}

    private readonly receive = (event: MessageEvent) => {
        const data = event.data;
        if (
            event.source === this.controller &&
            data?.kind === "glide-image-validation-failed" &&
            data.appID === this.appID &&
            data.revision === this.revision
        ) {
            this.stop();
            return;
        }

        if (
            !this.ready ||
            event.source !== this.controller ||
            data?.kind !== "glide-image-validation-resolve" ||
            data.appID !== this.appID ||
            data.revision !== this.revision ||
            typeof data.source !== "string" ||
            data.source.length > 8192
        )
            return;
        const port = event.ports[0];
        if (port === undefined) return;
        void this.session.resolveMedia(this.appID, data.source, data.target).then(url => {
            port.postMessage(this.ready && data.revision === this.revision ? url : data.source);
            port.close();
        });
    };
    private readonly imageFailed = (event: Event) => {
        if (!this.ready || !(event.target instanceof HTMLImageElement)) return;
        try {
            const url = new URL(event.target.currentSrc !== "" ? event.target.currentSrc : event.target.src);
            const endpoint = new URL(this.endpoint);
            if (
                url.origin === endpoint.origin &&
                url.pathname === endpoint.pathname &&
                url.searchParams.get("validationApp") === this.appID &&
                url.searchParams.get("validationRevision") === this.revision
            )
                this.stop();
        } catch (e: unknown) {
            /* Ignore unrelated resource failures. */
        }
    };
    private readonly controllerChanged = () => this.stop();

    private update(enabled: boolean, port?: MessagePort): void {
        this.controller?.postMessage(
            {
                kind: "glide-image-validation",
                protocol: 2,
                endpoint: this.endpoint,
                enabled,
                appID: this.appID,
                revision: this.revision,
                expiresAt: Date.now() + 10_000,
            },
            port === undefined ? [] : [port]
        );
    }

    public async start(): Promise<boolean> {
        if (bridges.has(this.appID) || this.starting !== undefined) return false;
        const generation = ++this.generation;
        this.starting = generation;
        try {
            return await this.startRun(generation);
        } finally {
            if (this.starting === generation) this.starting = undefined;
        }
    }

    private async startRun(generation: number): Promise<boolean> {
        const controller = this.workers.controller;
        if (controller === null || !(await this.session.start()) || generation !== this.generation) return false;
        this.revision = `${this.instanceID}-${generation}`;
        this.controller = controller;
        this.workers.addEventListener("message", this.receive);
        this.workers.addEventListener("controllerchange", this.controllerChanged);
        const channel = new MessageChannel();
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
            const acknowledged = await new Promise<boolean>(resolve => {
                timeout = setTimeout(() => resolve(false), 2000);
                channel.port1.onmessage = event =>
                    resolve(
                        event.data?.ready === true && event.data.protocol === 2 && event.data.endpoint === this.endpoint
                    );
                this.update(true, channel.port2);
            });
            if (generation !== this.generation) return false;
            if (!acknowledged || !this.session.active || this.workers.controller !== controller) {
                this.stop();
                return false;
            }
            this.ready = true;
            window.addEventListener("error", this.imageFailed, true);
            bridges.set(this.appID, this);
            refreshRendering(this.appID);
            this.timer = setInterval(() => {
                if (!this.session.active || this.workers.controller !== this.controller) this.stop();
                else this.update(true);
            }, 2000);
            return true;
        } catch (e: unknown) {
            if (generation === this.generation) this.stop();
            return false;
        } finally {
            if (timeout !== undefined) clearTimeout(timeout);
            channel.port1.close();
            channel.port2.close();
        }
    }

    public imageURL(source: string, retirementTarget?: () => string): string {
        if (!this.ready || !this.session.active) return source;
        const url = new URL(this.endpoint);
        try {
            const existing = new URL(source);
            if (
                existing.origin === url.origin &&
                existing.pathname === url.pathname &&
                existing.searchParams.get("validationApp") === this.appID &&
                existing.searchParams.get("validationRevision") === this.revision
            )
                return source;
        } catch (e: unknown) {
            return source;
        }
        const target = retirementTarget?.() ?? source;
        if (retirementTarget !== undefined) {
            if (parseImageValidationTarget(target, this.endpoint) === undefined) return source;
            url.searchParams.set("validationTarget", target);
        }
        url.searchParams.set("url", source.split("#", 1)[0]);
        url.searchParams.set("validationApp", this.appID);
        url.searchParams.set("validationRevision", this.revision);
        return url.href;
    }

    public stop(): void {
        this.generation++;
        this.starting = undefined;
        this.ready = false;
        window.removeEventListener("error", this.imageFailed, true);
        if (bridges.get(this.appID) === this) {
            bridges.delete(this.appID);
            refreshRendering(this.appID);
        }
        if (this.timer !== undefined) clearInterval(this.timer);
        this.timer = undefined;
        this.update(false);
        this.revision = "";
        this.session.stop();
        this.workers.removeEventListener("message", this.receive);
        this.workers.removeEventListener("controllerchange", this.controllerChanged);
        this.controller = undefined;
    }
}
